import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Pages
import HomePage from './pages/HomePage';
import JobSearchPage from './pages/JobSearchPage';
import JobSectorsPage from './pages/JobSectorsPage';
import JobLocationsPage from './pages/JobLocationsPage';
import JobDetailsPage from './pages/JobDetailsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import JobSeekerDashboard from './pages/JobSeekerDashboard';
import EmployerDashboard from './pages/EmployerDashboard';
import PostJobPage from './pages/PostJobPage';
import ApplicantsPage from './pages/ApplicantsPage';
import ProfilePage from './pages/ProfilePage';
import CareerAdvicePage from './pages/CareerAdvicePage';
import CoursesPage from './pages/CoursesPage';
import StudentServicesPage from './pages/StudentServicesPage';
import RecruiterPage from './pages/RecruiterPage';
import RecruitersDirectoryPage from './pages/RecruitersDirectoryPage';
import RecruiterProfilePage from './pages/RecruiterProfilePage';
import PricingPage from './pages/PricingPage';
import PurchaseServicesPage from './pages/PurchaseServicesPage';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import EventsPage from './pages/EventsPage';
import AdminDashboard from './pages/AdminDashboard';
import CVBuilderPage from './pages/CVBuilderPage';
import CourseProviderDashboard from './pages/CourseProviderDashboard';
import PostCoursePage from './pages/PostCoursePage';
import RecruitmentServicesDashboard from './pages/RecruitmentServicesDashboard';
import UniversitiesPage from './pages/UniversitiesPage';
import UniversityPage from './pages/UniversityPage';
import CourseProvidersPage from './pages/CourseProvidersPage';
import CourseProviderPage from './pages/CourseProviderPage';
import UniversityManagerDashboard from './pages/UniversityManagerDashboard';
import RecruiterDashboard from './pages/RecruiterDashboard';
import EventHostDashboard from './pages/EventHostDashboard';
import CourseDetailsPage from './pages/CourseDetailsPage';
import TermsPage from './pages/TermsPage';
import CookiesPage from './pages/CookiesPage';
import PrivacyPage from './pages/PrivacyPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import JobAlertsPage from './pages/JobAlertsPage';
import SkillsTestsPage from './pages/SkillsTestsPage';
import InterviewPrepPage from './pages/InterviewPrepPage';
import SavedJobsPage from './pages/SavedJobsPage';
import ApplicationTrackerPage from './pages/ApplicationTrackerPage';
import CandidatePipelinePage from './pages/CandidatePipelinePage';
import VideoInterviewPage from './pages/VideoInterviewPage';
import MessagesPage from './pages/MessagesPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import EmployerProfilePage from './pages/EmployerProfilePage';
import UserManualPage from './pages/UserManualPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import DirectRecruitmentPage from './pages/DirectRecruitmentPage';
import DirectEmploymentSolutionsPage from './pages/DirectEmploymentSolutionsPage';
import MyRecruitmentRequestsPage from './pages/MyRecruitmentRequestsPage';
import RecruitmentRequestDetailPage from './pages/RecruitmentRequestDetailPage';

// Protected Route Component
const ProtectedRoute = ({ children, allowedTypes = [] }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#003366] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedTypes.length > 0 && !allowedTypes.includes(user?.user_type)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Layout with Navbar and Footer
const Layout = ({ children, showFooter = true }) => (
  <>
    <Navbar />
    <main className="min-h-screen">{children}</main>
    {showFooter && <Footer />}
  </>
);

// Auth Layout (no navbar/footer)
const AuthLayout = ({ children }) => (
  <main>{children}</main>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      <Route path="/jobs" element={<Layout><JobSearchPage /></Layout>} />
      <Route path="/jobs/sectors" element={<Layout><JobSectorsPage /></Layout>} />
      <Route path="/jobs/locations" element={<Layout><JobLocationsPage /></Layout>} />
      <Route path="/jobs/:jobId" element={<Layout><JobDetailsPage /></Layout>} />
      <Route path="/career-advice" element={<Layout><CareerAdvicePage /></Layout>} />
      <Route path="/courses" element={<Layout><CoursesPage /></Layout>} />
      <Route path="/courses/:courseId" element={<Layout><CourseDetailsPage /></Layout>} />
      <Route path="/student-services" element={<Layout><StudentServicesPage /></Layout>} />
      <Route path="/recruiter" element={<Layout><RecruiterPage /></Layout>} />
      <Route path="/recruiters" element={<Layout><RecruitersDirectoryPage /></Layout>} />
      <Route path="/recruiters/:recruiterId" element={<Layout><RecruiterProfilePage /></Layout>} />
      <Route path="/direct-employment-solutions" element={<Layout><DirectEmploymentSolutionsPage /></Layout>} />
      <Route path="/salary-checker" element={<Layout><CareerAdvicePage /></Layout>} />
      <Route path="/pricing" element={<Layout><PricingPage /></Layout>} />
      <Route path="/services" element={<Layout><PurchaseServicesPage /></Layout>} />
      <Route path="/events" element={<Layout><EventsPage /></Layout>} />
      <Route path="/payment/success" element={<Layout><PaymentSuccessPage /></Layout>} />
      <Route path="/recruitment-services" element={<Layout><RecruiterPage /></Layout>} />
      <Route path="/skills-tests" element={<Layout><SkillsTestsPage /></Layout>} />
      <Route path="/interview-prep" element={<Layout><InterviewPrepPage /></Layout>} />
      <Route path="/blog" element={<Layout><BlogPage /></Layout>} />
      <Route path="/blog/:slug" element={<Layout><BlogPostPage /></Layout>} />
      <Route path="/employers/:employerId" element={<Layout><EmployerProfilePage /></Layout>} />
      <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
      <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
      <Route path="/help" element={<Layout><UserManualPage /></Layout>} />
      <Route path="/cookies" element={<Layout><CookiesPage /></Layout>} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<AuthLayout><LoginPage /></AuthLayout>} />
      <Route path="/register" element={<AuthLayout><RegisterPage /></AuthLayout>} />
      <Route path="/forgot-password" element={<AuthLayout><ForgotPasswordPage /></AuthLayout>} />
      <Route path="/reset-password" element={<AuthLayout><ResetPasswordPage /></AuthLayout>} />
      <Route path="/verify-email" element={<AuthLayout><VerifyEmailPage /></AuthLayout>} />
      
      {/* Job Seeker Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedTypes={['job_seeker', 'student']}>
          <Layout showFooter={false}><JobSeekerDashboard /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Employer Routes */}
      <Route path="/employer/dashboard" element={
        <ProtectedRoute allowedTypes={['employer']}>
          <Layout showFooter={false}><EmployerDashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employer/post-job" element={
        <ProtectedRoute allowedTypes={['employer', 'recruiter']}>
          <Layout showFooter={false}><PostJobPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employer/direct-recruitment" element={
        <ProtectedRoute allowedTypes={['employer']}>
          <Layout showFooter={false}><DirectRecruitmentPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employer/recruitment-requests" element={
        <ProtectedRoute allowedTypes={['employer']}>
          <Layout showFooter={false}><MyRecruitmentRequestsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employer/recruitment-requests/:id" element={
        <ProtectedRoute allowedTypes={['employer']}>
          <Layout showFooter={false}><RecruitmentRequestDetailPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employer/jobs/:jobId/applicants" element={
        <ProtectedRoute allowedTypes={['employer', 'recruiter']}>
          <Layout showFooter={false}><ApplicantsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employer/jobs/:jobId/edit" element={
        <ProtectedRoute allowedTypes={['employer']}>
          <Layout showFooter={false}><PostJobPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employer/pipeline" element={
        <ProtectedRoute allowedTypes={['employer']}>
          <CandidatePipelinePage />
        </ProtectedRoute>
      } />
      <Route path="/interview/:roomId" element={
        <ProtectedRoute>
          <VideoInterviewPage />
        </ProtectedRoute>
      } />
      
      {/* Shared Protected Routes */}
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout showFooter={false}><ProfilePage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/cv-builder" element={
        <ProtectedRoute allowedTypes={['job_seeker', 'student']}>
          <Layout showFooter={false}><CVBuilderPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/alerts" element={
        <ProtectedRoute allowedTypes={['job_seeker', 'student']}>
          <Layout showFooter={false}><JobAlertsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/saved-jobs" element={
        <ProtectedRoute allowedTypes={['job_seeker', 'student']}>
          <Layout showFooter={false}><SavedJobsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/my-applications" element={
        <ProtectedRoute allowedTypes={['job_seeker', 'student']}>
          <Layout showFooter={false}><ApplicationTrackerPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Layout showFooter={false}><MessagesPage /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Course post — redirect course providers to their dashboard */}
      <Route path="/courses/post" element={
        <ProtectedRoute allowedTypes={['course_provider', 'university_manager', 'admin']}>
          <Layout showFooter={false}><PostCoursePage /></Layout>
        </ProtectedRoute>
      } />

      {/* Course Provider Routes */}
      <Route path="/course-provider/dashboard" element={
        <ProtectedRoute allowedTypes={['course_provider']}>
          <Layout showFooter={false}><CourseProviderDashboard /></Layout>
        </ProtectedRoute>
      } />

      {/* Recruitment Services Dashboard (admin/recruiter) */}
      <Route path="/recruitment-dashboard" element={
        <ProtectedRoute allowedTypes={['admin', 'recruiter']}>
          <Layout showFooter={false}><RecruitmentServicesDashboard /></Layout>
        </ProtectedRoute>
      } />

      {/* Universities — public */}
      <Route path="/universities" element={<Layout><UniversitiesPage /></Layout>} />
      <Route path="/universities/:id" element={<Layout><UniversityPage /></Layout>} />

      {/* Course Providers/Trainers — public, deliberately separate from Universities */}
      <Route path="/course-providers" element={<Layout><CourseProvidersPage /></Layout>} />
      <Route path="/course-providers/:id" element={<Layout><CourseProviderPage /></Layout>} />

      {/* University Manager Dashboard */}
      <Route path="/university/dashboard" element={
        <ProtectedRoute allowedTypes={['university_manager', 'admin']}>
          <Layout showFooter={false}><UniversityManagerDashboard /></Layout>
        </ProtectedRoute>
      } />

      {/* Recruiter Dashboard */}
      <Route path="/recruiter-dashboard" element={
        <ProtectedRoute allowedTypes={['recruiter', 'admin']}>
          <Layout showFooter={false}><RecruiterDashboard /></Layout>
        </ProtectedRoute>
      } />

      {/* Event Host Dashboard */}
      <Route path="/event-host/dashboard" element={
        <ProtectedRoute allowedTypes={['event_host', 'admin']}>
          <Layout showFooter={false}><EventHostDashboard /></Layout>
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedTypes={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute allowedTypes={['admin']}>
          <Layout showFooter={false}><AdminAnalyticsPage /></Layout>
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
